import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS, getCurrentBrand } from '../config/api';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Avatar,
  Tooltip,
  Fade,
  Card,
  CardContent,
  Divider,
  Badge
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const Users = memo(() => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    roleId: 1
  });

  // Ref for dialog content to enable scrolling
  const dialogContentRef = useRef(null);

  // Handler to scroll focused element into view
  const handleFieldFocus = (e) => {
    if (dialogContentRef.current) {
      // Find the closest Grid item containing the focused field
      const gridItem = e.target.closest('.MuiGrid-item') || e.target.closest('[class*="MuiGrid"]');
      const fieldContainer = gridItem || e.target.closest('.MuiFormControl-root') || e.target;
      
      if (fieldContainer) {
        setTimeout(() => {
          fieldContainer.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  };

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.USERS}?brand=${getCurrentBrand()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        throw new Error('Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setSnackbar({
        open: true,
        message: t('users.errorLoadingUsers'),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const loadRoles = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ROLES, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  }, []);

  // Load users and roles on component mount
  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [loadUsers, loadRoles]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateUser = () => {
    setIsEditMode(false);
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      username: '',
      password: '',
      roleId: 1
    });
    setIsDialogOpen(true);
  };

  const handleEditUser = (user) => {
    setIsEditMode(true);
    setEditingUser(user);
    setFormData({
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      email: user.email || '',
      username: user.username || '',
      password: '',
      roleId: user.role_id || 1
    });
    setIsDialogOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(t('users.deleteConfirm'))) {
      return;
    }

    try {
      const baseUrl = API_ENDPOINTS.USER_BY_ID(userId);
      const brandParam = `brand=${getCurrentBrand()}`;
      const url = baseUrl.includes('?') ? `${baseUrl}&${brandParam}` : `${baseUrl}?${brandParam}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: t('users.userDeleted'),
          severity: 'success'
        });
        loadUsers();
      } else {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: t('users.errorDeletingUser'),
        severity: 'error'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.username) {
      setSnackbar({
        open: true,
        message: t('users.fillRequiredFields'),
        severity: 'error'
      });
      return;
    }

    if (!isEditMode && !formData.password) {
      setSnackbar({
        open: true,
        message: t('users.passwordRequiredNew'),
        severity: 'error'
      });
      return;
    }

    try {
      const baseUrl = isEditMode ? API_ENDPOINTS.USER_BY_ID(editingUser.id) : API_ENDPOINTS.USERS;
      const brandParam = `brand=${getCurrentBrand()}`;
      const url = baseUrl.includes('?') ? `${baseUrl}&${brandParam}` : `${baseUrl}?${brandParam}`;
      const method = isEditMode ? 'PUT' : 'POST';
      
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        username: formData.username,
        roleId: formData.roleId
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: isEditMode ? t('users.userUpdated') : t('users.userCreated'),
          severity: 'success'
        });
        setIsDialogOpen(false);
        loadUsers();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      setSnackbar({
        open: true,
        message: error.message || t('users.errorSavingUser'),
        severity: 'error'
      });
    }
  };

  const getRoleColor = (roleId) => {
    switch (roleId) {
      case 1: return '#27ae60'; // admin
      case 2: return '#f39c12'; // senior_admin
      case 3: return '#e74c3c'; // system_admin
      default: return '#95a5a6';
    }
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : t('users.unknown');
  };

  const getRoleIcon = (roleId) => {
    switch (roleId) {
      case 3: return <SecurityIcon sx={{ fontSize: 16 }} />;
      case 2: return <AdminIcon sx={{ fontSize: 16 }} />;
      case 1: return <PersonIcon sx={{ fontSize: 16 }} />;
      default: return <PersonIcon sx={{ fontSize: 16 }} />;
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
      <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 4, // rounded corners (16px)
            background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', // soft shadow
            minHeight: '60vh',
            border: '1px solid rgba(148, 163, 184, 0.12)', // subtle border
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.08) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
                radial-gradient(circle at 40% 40%, rgba(59, 130, 246, 0.03) 0%, transparent 50%)
              `,
              borderRadius: 'inherit',
              pointerEvents: 'none'
            }
          }}>
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' },
            gap: { xs: 2, sm: 0 }
          }}>
            <Box sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              borderRadius: 3, 
              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
              mr: { xs: 0, sm: 2 },
              boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)'
            }}>
              <PeopleIcon sx={{ color: 'white', fontSize: { xs: 28, sm: 32 } }} />
            </Box>
            <Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 800, 
                color: '#1e293b', 
                mb: 0.5,
                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {t('users.title')}
              </Typography>
              <Typography variant="h6" sx={{ 
                color: '#64748b', 
                fontWeight: 400,
                fontSize: { xs: '0.9rem', sm: '1.25rem' },
                lineHeight: { xs: 1.3, sm: 1.4 }
              }}>
                {t('users.subtitle')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Enhanced Action Bar */}
      <Fade in timeout={1000}>
        <Card sx={{ 
          mb: 3, 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(148, 163, 184, 0.12)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 3, sm: 0 }
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 2, sm: 3 },
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Badge badgeContent={users.length} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem', height: 20, minWidth: 20 } }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PeopleIcon sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />
                  </Box>
                </Badge>
                <Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 700, 
                    color: '#2c3e50', 
                    mb: 0.5,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    {t('users.systemUsers')}
                  </Typography>
                  
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                width: { xs: '100%', sm: 'auto' }
              }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadUsers}
                  disabled={isLoading}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    width: { xs: '100%', sm: 'auto' },
                    borderColor: '#60a5fa',
                    color: '#60a5fa',
                    '&:hover': {
                      borderColor: '#3b82f6',
                      backgroundColor: 'rgba(96, 165, 250, 0.1)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {t('common.refresh')}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateUser}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    width: { xs: '100%', sm: 'auto' },
                    background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
                    boxShadow: '0 4px 15px rgba(96, 165, 250, 0.4)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 50%, #1e3a8a 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(59, 130, 246, 0.6)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {t('users.createUser')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Enhanced Users Table */}
      <Fade in timeout={1200}>
        <Card sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(148, 163, 184, 0.12)'
        }}>
          {/* Desktop Table View */}
          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow sx={{ 
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
                  '& .MuiTableCell-head': {
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '0.5px'
                  }
                }}>
                  <TableCell>{t('users.user')}</TableCell>
                  <TableCell>{t('users.email')}</TableCell>
                  <TableCell>{t('users.username')}</TableCell>
                  <TableCell>{t('users.role')}</TableCell>
                  <TableCell>{t('common.created') || 'Created'}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>{t('users.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: '#667eea' }} />
                        <Typography variant="body2" sx={{ color: '#6c757d' }}>
                          {t('users.loading')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <PeopleIcon sx={{ fontSize: 48, color: '#6c757d', opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ color: '#6c757d', fontWeight: 500 }}>
                          {t('users.noUsersFound')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6c757d' }}>
                          {t('users.createFirstUser')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => (
                    <Fade in timeout={800 + (index * 100)} key={user.id}>
                      <TableRow 
                        sx={{ 
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.05)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                          },
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Avatar sx={{ 
                              bgcolor: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
                              width: 48, 
                              height: 48,
                              background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
                              boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)'
                            }}>
                              <PersonIcon sx={{ fontSize: 24 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                                {user.first_name} {user.last_name}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#2c3e50' }}>
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                            {user.username}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getRoleIcon(user.role_id)}
                            label={getRoleName(user.role_id)}
                            sx={{
                              backgroundColor: `${getRoleColor(user.role_id)}15`,
                              color: getRoleColor(user.role_id),
                              fontWeight: 600,
                              border: `2px solid ${getRoleColor(user.role_id)}30`,
                              borderRadius: 2,
                              '&:hover': {
                                backgroundColor: `${getRoleColor(user.role_id)}25`,
                                transform: 'scale(1.05)'
                              },
                              transition: 'all 0.3s ease'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: '#6c757d' }}>
                            {new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Tooltip title={t('users.editUser')} arrow>
                              <IconButton
                                size="medium"
                                onClick={() => handleEditUser(user)}
                                sx={{ 
                                  color: '#60a5fa',
                                  '&:hover': {
                                    backgroundColor: 'rgba(96, 165, 250, 0.1)',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('users.deleteUser')} arrow>
                              <IconButton
                                size="medium"
                                onClick={() => handleDeleteUser(user.id)}
                                sx={{ 
                                  color: '#e74c3c',
                                  '&:hover': {
                                    backgroundColor: 'rgba(231, 76, 60, 0.1)',
                                    transform: 'scale(1.1)',
                                    boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fade>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 2 }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
                <CircularProgress size={40} sx={{ color: '#667eea' }} />
                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                  {t('users.loading')}
                </Typography>
              </Box>
            ) : users.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 6 }}>
                <PeopleIcon sx={{ fontSize: 48, color: '#6c757d', opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: '#6c757d', fontWeight: 500 }}>
                  {t('users.noUsersFound')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6c757d' }}>
                  {t('users.createFirstUser')}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {users.map((user, index) => (
                  <Fade in timeout={800 + (index * 100)} key={user.id}>
                    <Card sx={{ 
                      borderRadius: 3,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      border: '1px solid rgba(148, 163, 184, 0.12)',
                      '&:hover': {
                        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        {/* User Header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar sx={{ 
                            bgcolor: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 50%, #1e40af 100%)',
                            width: 48, 
                            height: 48,
                            background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
                            boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)'
                          }}>
                            <PersonIcon sx={{ fontSize: 24 }} />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>
                              {user.first_name} {user.last_name}
                            </Typography>
                            <Chip
                              icon={getRoleIcon(user.role_id)}
                              label={getRoleName(user.role_id)}
                              size="small"
                              sx={{
                                backgroundColor: `${getRoleColor(user.role_id)}15`,
                                color: getRoleColor(user.role_id),
                                fontWeight: 600,
                                border: `2px solid ${getRoleColor(user.role_id)}30`,
                                borderRadius: 2,
                                fontSize: '0.75rem'
                              }}
                            />
                          </Box>
                        </Box>

                        {/* User Details */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ color: '#6c757d', mb: 0.5 }}>
                            {t('users.email')}
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 500, mb: 1.5 }}>
                            {user.email}
                          </Typography>

                          <Typography variant="body2" sx={{ color: '#6c757d', mb: 0.5 }}>
                            {t('users.username')}
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 500, mb: 1.5 }}>
                            {user.username}
                          </Typography>

                          <Typography variant="body2" sx={{ color: '#6c757d', mb: 0.5 }}>
                            {t('common.created') || 'Created'}
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#2c3e50', fontWeight: 500 }}>
                            {new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, pt: 1, borderTop: '1px solid rgba(148, 163, 184, 0.12)' }}>
                          <Tooltip title={t('users.editUser')} arrow>
                            <IconButton
                              size="medium"
                              onClick={() => handleEditUser(user)}
                              sx={{ 
                                color: '#60a5fa',
                                '&:hover': {
                                  backgroundColor: 'rgba(96, 165, 250, 0.1)',
                                  transform: 'scale(1.1)',
                                  boxShadow: '0 4px 12px rgba(96, 165, 250, 0.3)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('users.deleteUser')} arrow>
                            <IconButton
                              size="medium"
                              onClick={() => handleDeleteUser(user.id)}
                              sx={{ 
                                color: '#e74c3c',
                                '&:hover': {
                                  backgroundColor: 'rgba(231, 76, 60, 0.1)',
                                  transform: 'scale(1.1)',
                                  boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)'
                                },
                                transition: 'all 0.3s ease'
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                    </Card>
                  </Fade>
                ))}
              </Box>
            )}
          </Box>
        </Card>
      </Fade>

      {/* Professional Create/Edit User Dialog */}
      <Dialog 
        open={isDialogOpen} 
        onClose={() => setIsDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isFullScreen}
        scroll="paper"
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100vh',
            borderRadius: 4,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.2)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 0,
                background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
          color: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 2,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }
        }}>
          <Box sx={{ p: 4, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
              <Box sx={{ 
                p: 2, 
                borderRadius: 3, 
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}>
                {isEditMode ? <EditIcon sx={{ fontSize: 28 }} /> : <AddIcon sx={{ fontSize: 28 }} />}
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.5px' }}>
                  {isEditMode ? t('users.editUser') : t('users.createUser')}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                  {isEditMode ? t('users.editUserDesc') : t('users.createUserDesc')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent 
            ref={dialogContentRef}
            sx={{ 
              p: { xs: 2.5, sm: 4 },
              pt: { xs: 2, sm: 3 },
              flex: '1 1 auto',
              // On tablets/phones use full height and let content flex; on desktop constrain
              maxHeight: { xs: 'unset', sm: 'unset', md: '70vh' },
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0, 0, 0, 0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(96, 165, 250, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(96, 165, 250, 0.5)',
                },
              },
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ 
                color: '#2c3e50', 
                fontWeight: 600, 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <PersonIcon sx={{ fontSize: 20, color: '#667eea' }} />
                {t('users.personalInformation')}
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('users.firstName')}
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  onFocus={handleFieldFocus}
                  required
                  variant="outlined"
                  placeholder={t('users.enterFirstName')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      fontSize: '0.95rem',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': { color: '#60a5fa' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('users.lastName')}
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  required
                  variant="outlined"
                  placeholder={t('users.enterLastName')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      fontSize: '0.95rem',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': { color: '#60a5fa' }
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, mb: 3 }}>
              <Typography variant="h6" sx={{ 
                color: '#2c3e50', 
                fontWeight: 600, 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                <SecurityIcon sx={{ fontSize: 20, color: '#667eea' }} />
                {t('users.accountDetails')}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('users.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onFocus={handleFieldFocus}
                  required
                  variant="outlined"
                  placeholder={t('users.emailPlaceholder')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      fontSize: '0.95rem',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': { color: '#60a5fa' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('users.username')}
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  onFocus={handleFieldFocus}
                  required
                  variant="outlined"
                  placeholder={t('users.enterUsername')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      fontSize: '0.95rem',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': { color: '#60a5fa' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={isEditMode ? t('users.newPassword') : t('users.password')}
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  onFocus={handleFieldFocus}
                  required={!isEditMode}
                  variant="outlined"
                  placeholder={isEditMode ? t('users.passwordLeaveBlank') : t('users.enterPassword')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      fontSize: '0.95rem',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root': {
                      '&.Mui-focused': { color: '#60a5fa' }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ 
                    '&.Mui-focused': { color: '#60a5fa' },
                    fontSize: '0.95rem'
                  }}>{t('users.role')}</InputLabel>
                  <Select
                    value={formData.roleId}
                    onChange={(e) => handleInputChange('roleId', e.target.value)}
                    onFocus={handleFieldFocus}
                    label={t('users.role')}
                    sx={{
                      borderRadius: 3,
                      fontSize: '0.95rem',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#60a5fa',
                        borderWidth: 2
                      }
                    }}
                  >
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id} sx={{ py: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={2}>
                          {getRoleIcon(role.id)}
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {role.display_name || role.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)' }} />
          <DialogActions
            sx={{
              p: { xs: 2.5, sm: 4 },
              pt: { xs: 2, sm: 3 },
              gap: 2,
              justifyContent: 'flex-end',
              position: 'sticky',
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(248,250,252,0.85) 0%, rgba(248,250,252,1) 60%)',
              backdropFilter: 'saturate(180%) blur(6px)',
              borderTop: '1px solid rgba(0, 0, 0, 0.06)',
              zIndex: 1
            }}
          >
            <Button 
              onClick={() => setIsDialogOpen(false)}
              variant="outlined"
              size="large"
              sx={{
                borderRadius: 3,
                px: { xs: 3, sm: 4 },
                py: 1.5,
                minWidth: { xs: 'auto', sm: 120 },
                width: { xs: '100%', sm: 'auto' },
                borderColor: '#6c757d',
                color: '#6c757d',
                fontWeight: 600,
                fontSize: '0.95rem',
                '&:hover': {
                  borderColor: '#5a6268',
                  backgroundColor: 'rgba(108, 117, 125, 0.08)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(108, 117, 125, 0.2)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                borderRadius: 3,
                px: { xs: 3, sm: 4 },
                py: 1.5,
                minWidth: { xs: 'auto', sm: 140 },
                width: { xs: '100%', sm: 'auto' },
                background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%)',
                boxShadow: '0 6px 20px rgba(96, 165, 250, 0.4)',
                fontWeight: 600,
                fontSize: '0.95rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 50%, #1d4ed8 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 30px rgba(96, 165, 250, 0.6)'
                },
                '&:active': {
                  transform: 'translateY(0px)',
                  boxShadow: '0 4px 15px rgba(96, 165, 250, 0.4)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {isEditMode ? t('users.updateUser') : t('users.createUser')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
});

Users.displayName = 'Users';

export default Users;
